import { useMemo } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEOHead from "../../components/feature/SEOHead";

type ServiceItem = { number: string; title: string; calligraphy: string; intro: string; desc: string; features: string[] };

export default function Services() {
  const { t } = useTranslation();
  const services = t("servicesPage.services", { returnObjects: true }) as ServiceItem[];

  const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) || "https://willstrusts-junlawfirm.com";

  const jsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Estate Planning Services — Jun Law Office",
    "description": "Comprehensive estate planning services for immigrant families in Indiana: will drafting, trust formation, and prenuptial agreements.",
    "url": `${siteUrl}/services`,
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "item": {
          "@type": "Service",
          "name": "Will Drafting",
          "description": "Expert will drafting for immigrant families with cross-border assets, guardian designations, and overseas property provisions in Indiana.",
          "provider": { "@type": "LegalService", "name": "Law Office of Hong-min Jun" },
          "areaServed": { "@type": "State", "name": "Indiana" }
        }
      },
      {
        "@type": "ListItem",
        "position": 2,
        "item": {
          "@type": "Service",
          "name": "Trust Formation",
          "description": "Living trust and estate trust formation for probate avoidance, cross-border estate planning, and multi-generational wealth transfer.",
          "provider": { "@type": "LegalService", "name": "Law Office of Hong-min Jun" },
          "areaServed": { "@type": "State", "name": "Indiana" }
        }
      },
      {
        "@type": "ListItem",
        "position": 3,
        "item": {
          "@type": "Service",
          "name": "Prenuptial Agreement",
          "description": "Enforceable prenuptial agreements protecting pre-marital assets, overseas holdings, and business interests under Indiana law.",
          "provider": { "@type": "LegalService", "name": "Law Office of Hong-min Jun" },
          "areaServed": { "@type": "State", "name": "Indiana" }
        }
      }
    ]
  }), [siteUrl]);

  return (
    <div className="min-h-screen flex flex-col bg-[#EDEEF3]">
      <SEOHead
        title="Estate Planning Services | Wills, Trusts & Prenuptial Agreements | Jun Law Office"
        description="Comprehensive estate planning services for immigrant families in Indiana: will drafting, living trust formation, and prenuptial agreements. Multilingual support in English, Korean & Chinese."
        keywords="will drafting Indiana, living trust formation Indiana, prenuptial agreement Indiana, estate planning services immigrant families"
        canonicalPath="/services"
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="flex-1">
        <section className="relative pt-40 pb-24 bg-[#EDEEF3] overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-20 -right-20 w-[500px] h-[500px] rounded-full border border-[#1E202A]/[0.03]" />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6">
            <span className="text-[#8A7A6A] block leading-none mb-2" style={{ fontFamily: "'Great Vibes', cursive", fontSize: "44px" }}>
              {t("servicesPage.heroCalligraphy")}
            </span>
            <h1 className="text-[#1E202A] leading-tight max-w-2xl mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(38px, 5vw, 62px)", fontWeight: 300 }}>
              {t("servicesPage.heroH1Line1")}
              <br />
              <em>{t("servicesPage.heroH1Line2")}</em>
            </h1>
            <p className="text-[#74758A] text-sm leading-[2] max-w-lg">{t("servicesPage.heroDesc")}</p>
          </div>
        </section>

        {Array.isArray(services) && services.map((s, i) => (
          <section key={i} className={`py-24 ${i % 2 === 0 ? "bg-[#E4E5EC]" : "bg-[#EDEEF3]"}`}>
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                <div className="lg:col-span-4">
                  <span className="text-[#8A7A6A]/15 leading-none block mb-2 select-none" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "100px", fontStyle: "italic", fontWeight: 300 }}>
                    {s.number}
                  </span>
                  <span className="text-[#8A7A6A]/50 block leading-none mb-4" style={{ fontFamily: "'Great Vibes', cursive", fontSize: "26px" }}>
                    {s.calligraphy}
                  </span>
                  <h2 className="text-[#1E202A] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(28px, 3vw, 40px)", fontWeight: 400 }}>
                    {s.title}
                  </h2>
                  <Link to="/contact" className="inline-flex items-center gap-2 bg-[#1E202A] text-[#EDEEF3] px-8 py-3.5 rounded-full text-sm font-medium hover:bg-[#8A7A6A] transition-all duration-300 cursor-pointer whitespace-nowrap" style={{ letterSpacing: "0.02em" }}>
                    {t("servicesPage.inquireCta")}
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
                <div className="lg:col-span-8">
                  <p className="text-[#1E202A]/75 leading-relaxed italic mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "18px", fontWeight: 300 }}>{s.intro}</p>
                  <p className="text-[#74758A] text-sm leading-[2] mb-12">{s.desc}</p>
                  <div className="w-full h-px bg-[#D0D1DB] mb-10" />
                  <p className="text-[#8A7A6A]/60 block leading-none mb-6" style={{ fontFamily: "'Great Vibes', cursive", fontSize: "24px" }}>
                    {t("servicesPage.includedCalligraphy")}
                  </p>
                  <div className="flex flex-col gap-0">
                    {Array.isArray(s.features) && s.features.map((f, fi) => (
                      <div key={fi} className="flex items-start gap-6 py-4 border-b border-[#D0D1DB]/60 last:border-b-0">
                        <span className="text-[#8A7A6A]/35 shrink-0 mt-0.5 select-none" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "13px", fontWeight: 300, minWidth: "22px" }}>
                          {String(fi + 1).padStart(2, "0")}
                        </span>
                        <span className="text-[#1E202A]/80 text-sm leading-relaxed">{f}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        ))}

        <section className="py-28 bg-[#D9DAE5]">
          <div className="max-w-3xl mx-auto px-6 text-center relative">
            <span className="absolute inset-0 flex items-center justify-center text-[#1E202A]/[0.03] select-none pointer-events-none leading-none" style={{ fontFamily: "'Great Vibes', cursive", fontSize: "200px" }}>Legacy</span>
            <div className="relative z-10">
              <span className="text-[#8A7A6A] block leading-none mb-2" style={{ fontFamily: "'Great Vibes', cursive", fontSize: "40px" }}>{t("servicesPage.ctaCalligraphy")}</span>
              <h2 className="text-[#1E202A] mb-5" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(26px, 3.5vw, 40px)", fontWeight: 300 }}>
                {t("servicesPage.ctaTitle1")}<br /><em>{t("servicesPage.ctaTitle2")}</em>
              </h2>
              <p className="text-[#74758A] text-sm leading-[2] mb-8 max-w-md mx-auto">{t("servicesPage.ctaDesc")}</p>
              <Link to="/contact" className="inline-flex items-center gap-2 bg-[#1E202A] text-[#EDEEF3] px-10 py-4 rounded-full text-sm font-medium hover:bg-[#8A7A6A] transition-all duration-300 cursor-pointer whitespace-nowrap" style={{ letterSpacing: "0.02em" }}>
                {t("servicesPage.ctaBtn")}<i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
